import { FC, useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useDeepCompareMemo, useMountedEffect } from '@clatter/platform';
import { CircularLoader } from '../../../shared';
import { useFolderManager } from '../../../hooks';
import { FolderManagerMimeTypes } from '../../../interfaces';
import { useGetUserRootFoldersQuery } from '../../../services';
import { getIconForMimeType } from '../../../helpers';

const StyledFolderList = styled.ul`
  margin: 0;
  padding: 0;
  width: 100%;
  list-style: none;
`;

const StyledFolderListItem = styled.li`
  cursor: pointer;
  width: 100%;
  display: flex;
  gap: 10px;
  justify-content: flex-start;
  align-items: center;
  flex-direction: row;
  min-height: 30px;
  padding: 5px 10px;

  user-select: none;

  &:hover,
  &.selected {
    background-color: #c5c5c5;
    border-radius: 10px;
  }

  &.selected {
    background-color: #00a8f7;
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.5;
  }
`;
const StyledHeader = styled.div`
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
  margin-bottom: 10px;
`;

const StyledHeaderLink = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 1.4rem;

  &:hover {
    text-decoration: underline;
  }

  &.disabled {
    pointer-events: none;
  }
`;

interface IHeader {
  breadcrumbs: FolderManagerItem[];
  onBackClick: (item: FolderManagerItem) => void;
}

interface FolderHierarchyPicker {
  rootFolder: FolderManagerItem | null;
  disabledIds: number[];
  includeUserRoot?: boolean;
  selectedFolder: FolderManagerItem | null;
  onSelect: (item: FolderManagerItem | null) => void;
  onChange: (item: FolderManagerItem | null) => void;
}

const Header: FC<IHeader> = ({ breadcrumbs, onBackClick }) => {
  const rootLink = breadcrumbs[0];
  const backLink = breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 1] : null;

  return (
    <StyledHeader>
      {backLink ? (
        <StyledHeaderLink onClick={() => onBackClick(backLink)}>
          <ArrowBackIcon />
          <span>{backLink?.name}</span>
        </StyledHeaderLink>
      ) : (
        <StyledHeaderLink className="disabled">
          <span>{rootLink?.name}</span>
        </StyledHeaderLink>
      )}
    </StyledHeader>
  );
};

const FolderList = ({
  data,
  disabledIds,
  onDoubleClick,
  onSelect,
}: {
  data: FolderManagerItem[];
  disabledIds: number[];
  onSelect: (item: FolderManagerItem | null) => void;
  onDoubleClick: (item: FolderManagerItem) => void;
}) => {
  const [selectedRow, setSelectedRow] = useState<FolderManagerItem | null>(null);

  const handleClick = (item: FolderManagerItem) => {
    if (!selectedRow || selectedRow.id !== item.id) {
      setSelectedRow(item);
      onSelect(item);
      return;
    }
    onSelect(null);
    setSelectedRow(null);
  };

  return (
    <StyledFolderList>
      {data?.map((item) => {
        return (
          <StyledFolderListItem
            key={item?.id}
            className={classNames({
              disabled: item?.type !== FolderManagerMimeTypes.FOLDER || disabledIds?.includes(+item?.id),
              selected: selectedRow?.id === item?.id,
            })}
            onClick={() => handleClick(item)}
            onDoubleClick={() => onDoubleClick(item)}
          >
            {getIconForMimeType(item?.type)}
            <span>{item?.displayName || item?.name}</span>
          </StyledFolderListItem>
        );
      })}
    </StyledFolderList>
  );
};

export const FolderHierarchyPicker: FC<FolderHierarchyPicker> = ({
  rootFolder,
  selectedFolder,
  disabledIds,
  onChange,
  onSelect,
  includeUserRoot = false,
}) => {
  const [showRootHierarchy, setShowRootHierarchy] = useState(includeUserRoot);

  const {
    hierarchyData,
    breadcrumbs,
    isLoading: isFoldersLoading,
    handleFolderClick,
  } = useFolderManager({
    rootFolderId: includeUserRoot ? null : rootFolder ? rootFolder.id : null,
    selectedFolderId: selectedFolder?.id,
    inMemory: true,
    onlyFolders: true,
  });

  const { data: usersRootFoldersData, isLoading: isUsersRootFoldersData } = useGetUserRootFoldersQuery(undefined, {
    skip: !includeUserRoot,
  });

  const isLoading = isUsersRootFoldersData || isFoldersLoading;

  //region COMPUTED
  const rootFoldersHierarchy: FolderManagerItem[] = useDeepCompareMemo(() => {
    const userRoot = (usersRootFoldersData || []).find((folder) => folder.displayName?.toLowerCase() === 'my assets');
    return [userRoot, rootFolder].filter(Boolean);
  }, [usersRootFoldersData, rootFolder]);

  const augmentedBreadcrumbs: FolderManagerItem[] = useDeepCompareMemo(() => {
    // Note: This is only a placeholder to ensure navigation works properly.
    // Ideally, this should be handled in the API, but for now, we need to support the workspaces case.
    const baseBreadcrumb = [{ id: 0, name: '' }];

    if (showRootHierarchy) {
      return baseBreadcrumb;
    }

    // because of unique definition of workspace path we need to do some augmenting here at least for now
    if (includeUserRoot && breadcrumbs?.length === 1 && breadcrumbs?.[0]?.id === rootFolder?.id) {
      return [baseBreadcrumb, breadcrumbs[0]];
    }

    return breadcrumbs;
  }, [showRootHierarchy, breadcrumbs]);
  //endregion

  //region METHODS
  const handleBackClick = (item: FolderManagerItem) => {
    const index = (augmentedBreadcrumbs || []).findIndex((breadcrumb) => breadcrumb.id === item.id);
    const previousItem = augmentedBreadcrumbs?.[index - 1];

    if (!previousItem) {
      console.warn('Warning previous breadcrumb item not found');
      return;
    }

    const rootIds = rootFoldersHierarchy.map((folder) => folder.id);

    if (includeUserRoot && rootIds.includes(+item.id) && rootFolder) {
      handleFolderClick({ item: rootFolder });
      setShowRootHierarchy(true);
      return;
    }

    handleFolderClick({ item: previousItem });
    onChange(item);
  };

  const handleDoubleClick = (item: FolderManagerItem) => {
    if (item.type === FolderManagerMimeTypes.FOLDER) {
      if (includeUserRoot && showRootHierarchy) {
        setShowRootHierarchy(false);
      }

      handleFolderClick({ item: item });
      onChange(item);
      return;
    }
  };
  //endregion

  // disable selecting not picked folder
  useMountedEffect(() => {
    if (showRootHierarchy) {
      onChange(null);
    }
  }, [showRootHierarchy, rootFolder?.id]);

  return (
    <div>
      <Header breadcrumbs={augmentedBreadcrumbs} onBackClick={handleBackClick} />

      {isLoading ? (
        <CircularLoader />
      ) : (
        <FolderList
          disabledIds={disabledIds}
          data={showRootHierarchy ? rootFoldersHierarchy : hierarchyData}
          onDoubleClick={handleDoubleClick}
          onSelect={onSelect}
        />
      )}
    </div>
  );
};
